// src/components/blog/SharedListsForCouples.js

import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import blogHeroImage from '../../assets/blog-shared-lists-couples.png';

function SharedListsForCouples() {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Shared Grocery & Packing Lists for Couples",
        "description": "The best free shared list app for couples. Keep grocery lists, packing lists, and to-dos in sync — plus wish lists for birthdays and holidays. Free.",
        "author": { "@type": "Organization", "name": "ListLink" },
        "publisher": { "@type": "Organization", "name": "ListLink" },
        "url": "https://list-link.com/blog/shared-lists-for-couples",
        "datePublished": "2026-02-22",
        "dateModified": "2026-02-22",
        "image": "https://list-link.com/og-image.png"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is ListLink free for couples?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. ListLink is completely free with no subscription fees or premium features. Both partners can use all features at no cost."
            }
          },
          {
            "@type": "Question",
            "name": "Do shared lists sync in real time?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Lists sync across your devices. When one person adds or checks off an item, the other will see it when they open the app."
            }
          },
          {
            "@type": "Question",
            "name": "What kinds of lists can couples share?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "You can share grocery lists, packing lists, to-do lists, and gift wish lists — all in the same app. No need to switch between multiple apps."
            }
          },
          {
            "@type": "Question",
            "name": "Can we use ListLink for birthdays and holidays too?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. ListLink also supports gift wish lists and events like Secret Santa or Sinterklaas. Your partner can secretly buy from your wish list without you seeing what they chose — surprises stay intact."
            }
          }
        ]
      }
    ]
  };

  return (
    <div>
      <Helmet>
        <title>Shared Grocery & Packing Lists for Couples | ListLink</title>
        <meta
          name="description"
          content="The best free shared list app for couples. Keep grocery lists, packing lists, and to-dos in sync — plus wish lists for birthdays and holidays. Free."
        />
        <meta
          name="keywords"
          content="shared grocery list app, couples list app, shared to-do list, packing list app free, shared list app for couples, collaborative list app, real-time shared list"
        />
        <meta property="og:title" content="Shared Grocery & Packing Lists for Couples | ListLink" />
        <meta property="og:description" content="The best free shared list app for couples. Keep grocery lists, packing lists, and to-dos in sync — plus wish lists for birthdays and holidays." />
        <meta property="og:url" content="https://list-link.com/blog/shared-lists-for-couples" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://list-link.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://list-link.com/blog/shared-lists-for-couples" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ backgroundColor: '#FDFAF5', pt: 6, pb: 4 }}>
        <Container maxWidth="md">
          <Typography variant="caption" sx={{ color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            February 22, 2026
          </Typography>
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mt: 1 }}>
            Shared Grocery & Packing Lists for Couples
          </Typography>
          <Typography variant="h6" sx={{ color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 400, lineHeight: 1.6, mb: 4 }}>
            Stop texting each other "can you add milk to the list?" Use a shared list app instead.
          </Typography>
          <Box
            component="img"
            src={blogHeroImage}
            alt="Couple sharing grocery and packing lists on mobile devices"
            sx={{
              width: '100%',
              height: { xs: 220, md: 320 },
              objectFit: 'cover',
              objectPosition: 'center 40%',
              display: 'block',
              borderRadius: 3,
            }}
          />
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" paragraph>
          If you live with a partner, you've experienced the pain of uncoordinated lists. One person writes the grocery list on paper, the other adds items via text. Someone forgets the list at home. You both buy milk. The packing list for vacation exists in three different Notes apps. Sound familiar?
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Why Couples Need a Shared List App
        </Typography>
        <Typography variant="body1" paragraph>
          A shared list app solves a simple but persistent problem: keeping two people on the same page. When both partners can add, check off, and rearrange items on the same list, the daily friction of household coordination disappears.
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li><strong>Grocery lists</strong> — Add items throughout the week as you run out. When either person goes to the store, the list is always current.</li>
            <li><strong>Packing lists</strong> — Plan together for trips. See what's packed and what's still needed without asking "did you grab the chargers?"</li>
            <li><strong>To-do lists</strong> — Household tasks, errands, and projects in one shared view. No more "I thought you were doing that."</li>
            <li><strong>Wish lists</strong> — Birthdays and holidays are easier when your partner can see what you actually want (or secretly buy from your list).</li>
          </ul>
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          What Makes a Good Shared List App
        </Typography>
        <Typography variant="body1" paragraph>
          There are dozens of list apps, but most are designed for solo use and bolt on sharing as an afterthought. The best shared list app for couples should be:
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li><strong>Fast to open and use</strong> — If it takes more than 5 seconds to add an item, you'll default back to texting.</li>
            <li><strong>Syncs across devices</strong> — Changes sync so both phones stay up to date.</li>
            <li><strong>Multiple list types</strong> — Grocery, packing, to-do, and wish lists in one app instead of four separate apps.</li>
            <li><strong>Easy sharing</strong> — Invite your partner by email and you're done. No complicated setup.</li>
            <li><strong>Free</strong> — Shared lists are a basic need, not a premium feature.</li>
          </ul>
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          How ListLink Works for Couples
        </Typography>
        <Typography variant="body1" paragraph>
          <Link to="/">ListLink</Link> lets you create any kind of list and share it with your partner instantly. Create a "Groceries" list, share it, and both of you can add and check off items. Heading on vacation? Create a "Trip to Barcelona" packing list and collaborate on what to bring.
        </Typography>
        <Typography variant="body1" paragraph>
          What makes ListLink particularly useful for couples is that it's also a gift list app. When birthdays or holidays come around, you can create wish lists within the same app. Your partner can secretly mark items as purchased from your list — you'll never see what they bought, but they'll know exactly what you wanted. <Link to="/learn-more">See how all of ListLink's features work</Link>.
        </Typography>
        <Typography variant="body1" paragraph>
          One app for daily lists and special occasions. No switching between apps, no subscription fees.
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          Get Started in Under a Minute
        </Typography>
        <Typography variant="body1" paragraph>
          Create a free account, make your first list, and invite your partner. You'll wonder how you managed without it.
        </Typography>

        {/* FAQ Section */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 6 }}>
          Frequently Asked Questions
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            Is ListLink free for couples?
          </Typography>
          <Typography variant="body1" paragraph>
            Yes. ListLink is completely free with no subscription fees or premium features. Both partners can use all features at no cost.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            Do shared lists sync in real time?
          </Typography>
          <Typography variant="body1" paragraph>
            Lists sync across your devices. When one person adds or checks off an item, the other will see it when they open the app.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            What kinds of lists can couples share?
          </Typography>
          <Typography variant="body1" paragraph>
            You can share grocery lists, packing lists, to-do lists, and gift wish lists — all in the same app. No need to switch between multiple apps.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            Can we use ListLink for birthdays and holidays too?
          </Typography>
          <Typography variant="body1" paragraph>
            Yes. ListLink also supports gift wish lists and events like Secret Santa or Sinterklaas. Your partner can secretly buy from your wish list without you seeing what they chose — surprises stay intact.
          </Typography>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: 6, p: 4, backgroundColor: '#0079bf', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
            Start sharing lists with your partner today
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={Link}
            to="/register"
          >
            Sign Up Free
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button component={Link} to="/blog" color="primary">
            &larr; Back to Blog
          </Button>
        </Box>
      </Container>
    </div>
  );
}

export default SharedListsForCouples;
